import { EnvironmentInjector, inject } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import {
  catchError,
  from,
  Observable,
  Subject,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { SelfUserStore } from '@domains/person/user/self/data';
import { SKIP_FORBIDDEN_ELEVATION } from '@application/data-access';
import { APP_CONFIG } from '@application/config';

/**
 * Marks a request that is being replayed after a temporary-auth elevation. When
 * set, this interceptor will NOT open the modal again for a subsequent 403 —
 * the (still) forbidden response is propagated instead, so a privileged user
 * that also lacks the permission (or a genuinely forbidden resource) cannot loop
 * the modal.
 */
export const IS_FORBIDDEN_RETRY = new HttpContextToken<boolean>(() => false);

/**
 * Shared across concurrent 403s so a single modal serves them all: only the
 * first forbidden response opens the modal; the rest wait on `elevationSubject`
 * and replay (or fail) once elevation settles.
 */
let isElevating = false;
const elevationSubject = new Subject<boolean>();

/**
 * Whether `url` goes to the service at `baseUrl`: the base itself or a path or query under it. A
 * bare `startsWith(baseUrl)` would also take `https://ato.testing` for `https://ato.test`.
 */
function isServiceRequest(url: string, baseUrl: string | null | undefined): boolean {
  if (!baseUrl) return false;
  const base = baseUrl.replace(/\/+$/, '');
  return url === base || url.startsWith(`${base}/`) || url.startsWith(`${base}?`);
}

/**
 * Whether a 403 names the permission the caller is missing — `{ "Permission": 326, "Message": … }`
 * (devolutiva 18, item 153; devolutiva 19, item 156). Only that refusal is lifted by another person's
 * login: the supervisor's token carries the supervisor's own role and permissions.
 */
function namesMissingPermission(error: HttpErrorResponse): boolean {
  return typeof error.error?.Permission === 'number';
}

/**
 * On HTTP 403, opens the temporary-auth modal (via `SelfUserStore`) to obtain a
 * privileged login and replays the original request with the elevated token.
 *
 * Must be registered BEFORE `TokenInterceptor` so the replayed request flows
 * back down through `TokenInterceptor.addAuthToken` and picks up the temporary
 * Bearer token started by `beginTemporaryAuthSession`.
 *
 * A request marked with `SKIP_FORBIDDEN_ELEVATION` (`withoutForbiddenElevation()`
 * from `@application/data-access`) is left alone: its 403 is an expected answer
 * that no supervisor login can unlock — signing in as another user of the same
 * tenant never puts that tenant in a live event's audience — so the error reaches
 * the caller instead of interrupting a screen the user never acted on.
 *
 * The support console's two services — the ato (`APP_CONFIG.apiUrl.ato_service`) and the
 * SupportService (`support_service`) — only get the modal when the 403 body names the
 * missing permission (`{ "Permission": 333, "Message": … }`). Their other 403s — "Você não
 * é agente deste espaço.", "Só quem pediu a sugestão registra o desfecho.", "Você não
 * participa desta conversa." — say the caller is not the right person, and no other login
 * changes that, so they reach the caller.
 *
 * The SupportService joined with devolutiva 19 (item 156, supportservice d3837bb): with
 * `PermissionsEnforced` off, as in production today, `SupportPermissions.RequireManagerAsync`
 * refuses a desk Member by ROLE with the number of the permission that will replace the
 * role (`PermissionDeniedException`), so the refusal a supervisor lifts carries `Permission`
 * whether the keys are on or off. Every other service keeps the old rule (any 403 asks).
 */
export const ForbiddenInterceptor: HttpInterceptorFn = (
  request: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  // Capture the environment injector synchronously (we are inside an injection
  // context here). `SelfUserStore` is then resolved from it lazily, only when a
  // 403 actually needs elevation (see `handle403Error`). Resolving the store
  // up-front on every request would force `SelfUserStore` (→ `AuthStore`)
  // construction during bootstrap — before the app has rendered — which can
  // close a DI cycle (NG0200). By the time a real 403 arrives those stores are
  // long constructed, so the lazy `injector.get` is cheap and cycle-free.
  const injector = inject(EnvironmentInjector);
  // A plain value provider set at bootstrap (`AppConfigProvider`), with no store behind it:
  // injecting it per request closes no DI cycle, as `TokenInterceptor` and
  // `SentryHttpErrorInterceptor` in this same lib already do.
  const { ato_service: atoUrl, support_service: supportUrl } = inject(APP_CONFIG).apiUrl;

  /** A supervisor login can lift this 403 (see the support console rule in the JSDoc above). */
  const elevationCanLift = (error: HttpErrorResponse): boolean =>
    !(isServiceRequest(request.url, atoUrl) || isServiceRequest(request.url, supportUrl)) ||
    namesMissingPermission(error);

  // `HttpContext` is mutable and shared by reference across clones, so setting the
  // marker in place carries it over to the clone `TokenInterceptor` makes.
  const markForbiddenRetry = (req: HttpRequest<any>): HttpRequest<any> => {
    req.context.set(IS_FORBIDDEN_RETRY, true);
    return req;
  };

  const replay = (): Observable<HttpEvent<any>> =>
    next(markForbiddenRetry(request));

  const waitForElevation = (
    error: HttpErrorResponse
  ): Observable<HttpEvent<any>> =>
    elevationSubject.pipe(
      take(1),
      switchMap((granted) => (granted ? replay() : throwError(() => error)))
    );

  const handle403Error = (
    error: HttpErrorResponse
  ): Observable<HttpEvent<any>> => {
    if (isElevating) {
      return waitForElevation(error);
    }

    // Resolve lazily, only now that a 403 truly needs elevation. Guarded so a
    // transient resolution failure just propagates the original 403.
    const selfUserStore = (() => {
      try {
        return injector.get(SelfUserStore);
      } catch (error) {
        console.error('ForbiddenInterceptor: Failed to resolve SelfUserStore', error);
        return null;
      }
    })();

    if (!selfUserStore) {
      return throwError(() => error);
    }

    isElevating = true;

    return from(selfUserStore.requestTemporaryElevation()).pipe(
      switchMap((granted) => {
        isElevating = false;
        elevationSubject.next(granted);
        return granted ? replay() : throwError(() => error);
      }),
      catchError(() => {
        isElevating = false;
        elevationSubject.next(false);
        return throwError(() => error);
      })
    );
  };

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      if (
        error.status === 403 &&
        request.withCredentials &&
        !request.context.get(IS_FORBIDDEN_RETRY) &&
        !request.context.get(SKIP_FORBIDDEN_ELEVATION) &&
        elevationCanLift(error)
      ) {
        return handle403Error(error);
      }
      return throwError(() => error);
    })
  );
};
